// ── Account Settings › Notifications (TPT460) ──
//
// Account-wide email preferences, the middle layer of the TPT459 resolution
// (api/src/lib/notification-preferences.js): a per-project personal choice wins,
// then this account choice, then the project-wide default. Backed by
// GET/PATCH /api/auth/me/notification-settings, which stores the
// notification_settings row with project_id = 0. `null` means "inherit the
// project default" and is shown as the "Project default" option.
//
// Pure (no DOM, no API client import) so node --test can exercise it directly;
// pages/security-settings.js owns rendering into the page and binding.

// Mirrors api/src/lib/notification-modes.js — the browser bundle can't require()
// the server lib (same hand-mirroring as pages/settings.js's mode list).
export const ACCOUNT_NOTIFICATION_MODES = [
  { value: 'off', label: 'Off' },
  { value: 'all', label: 'All Tasks' },
  { value: 'assigned', label: 'Assigned To Me' },
  { value: 'authored', label: 'Authored by Me' },
];

export const PROJECT_DEFAULT_VALUE = '';

export const ACCOUNT_NOTIFICATION_FIELDS = [
  {
    field: 'digest_mode',
    id: 'account-digest-mode',
    label: 'Daily digest',
    hint: 'One summary email per day of board activity in each project.',
  },
  {
    field: 'transactional_mode',
    id: 'account-transactional-mode',
    label: 'Task updates',
    hint: "Emails about changes to tasks you're subscribed to, sent shortly after they happen.",
  },
];

// Account tabs and the hash route each one lives at. #/account/settings is the
// user-menu entry and lands on Notifications; #/account/notifications is the
// stable deep link for emails; #/account/security predates the tab strip.
export const ACCOUNT_TABS = ['notifications', 'security'];
export const ACCOUNT_TAB_LABELS = { notifications: 'Notifications', security: 'Security' };
export const ACCOUNT_TAB_ROUTES = { notifications: '/account/notifications', security: '/account/security' };

export function accountTabForPath(path) {
  const clean = String(path || '').split('?')[0];
  return clean === '/account/security' ? 'security' : 'notifications';
}

// '' (the "Project default" option) is sent as null so the API clears the
// account override instead of rejecting an empty string.
export function selectValueToMode(value) {
  return value === PROJECT_DEFAULT_VALUE || value == null ? null : value;
}

export function patchAccountNotificationSetting(client, field, value) {
  return client.patch('/auth/me/notification-settings', { [field]: selectValueToMode(value) });
}

function renderSelect(f, current) {
  const selected = current == null ? PROJECT_DEFAULT_VALUE : current;
  const options = [{ value: PROJECT_DEFAULT_VALUE, label: 'Project default' }, ...ACCOUNT_NOTIFICATION_MODES];
  return `<div class="form-group account-notif-field">
      <label class="form-label" for="${f.id}">${f.label}</label>
      <select class="form-select" id="${f.id}" data-field="${f.field}">
        ${options.map(o => `<option value="${o.value}"${o.value === selected ? ' selected' : ''}>${o.label}</option>`).join('')}
      </select>
      <p class="text-muted">${f.hint}</p>
    </div>`;
}

// settings: { digest_mode, transactional_mode } with null = inherit, or null
// while loading failed (error card with a Retry button instead).
export function buildAccountNotificationsCard(settings, { error = false } = {}) {
  if (error) {
    return `<div class="card">
    <h3>Email notifications</h3>
    <p class="login-error" id="account-notif-error">Couldn't load your notification preferences.</p>
    <button class="btn btn-ghost mt-2" type="button" id="account-notif-retry">Retry</button>
  </div>`;
  }
  const s = settings || {};
  return `<div class="card">
    <h3>Email notifications</h3>
    <p class="text-muted mb-2">These apply to all your projects. A project's own Notifications tab can override them for that project. Leave a setting on <strong>Project default</strong> to follow each project's default.</p>
    <div class="account-notif-fields">
      ${ACCOUNT_NOTIFICATION_FIELDS.map(f => renderSelect(f, s[f.field])).join('')}
    </div>
    <p class="text-muted account-notif-order">Most specific wins: your setting in a project › these account settings › the project default.</p>
  </div>`;
}
